/** Client diagnostics trail (2026-09-13).
 *
 * Tom: "i tried again adding an expense to 1822 in deals and its not working"
 * — from a fresh tab the same add works every time, so whatever went wrong
 * lived only in HIS tab, and nothing about it reached the server. This is
 * the fix for that blindness: the app keeps a small ring buffer of the
 * events that matter (a ledger write and whether it was accepted, an outbox
 * rejection and its reason, a delivery merge, a settings refetch, a build
 * reload) and ships it to the backend every few seconds, per user. No
 * amounts, no descriptions, no addresses — shapes, counts and reasons only.
 *
 * Read side: rec_org_settings DIAG_<uid8> (org-scoped, so an admin can pull
 * a teammate's trail from the app or straight from the table). */
import { API_BASE } from "../config";

export interface DiagEvent { t: number; k: string; d?: Record<string, unknown> }

const MAX = 80;
const LS = "crm2_diag_v1";
const buf: DiagEvent[] = [];
let tokenFn: (() => string | null) | null = null;
let build: string | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let dirty = false;
let lastPushAt = 0;

function load(): void {
  try {
    const raw = localStorage.getItem(LS);
    if (!raw) return;
    const v: unknown = JSON.parse(raw);
    if (Array.isArray(v)) for (const e of v.slice(-MAX)) if (e && typeof e === "object" && typeof (e as DiagEvent).k === "string") buf.push(e as DiagEvent);
  } catch { /* private mode / junk */ }
}
load();

/** Wire the sender once the session exists (app boot). Safe to call again. */
export function diagStart(getToken: () => string | null, buildId: string | null): void {
  tokenFn = getToken;
  build = buildId;
}

/** Record one event. Cheap; never throws. */
export function diag(k: string, d?: Record<string, unknown>): void {
  try {
    const ev: DiagEvent = d ? { t: Date.now(), k, d } : { t: Date.now(), k };
    buf.push(ev);
    if (buf.length > MAX) buf.splice(0, buf.length - MAX);
    try { localStorage.setItem(LS, JSON.stringify(buf)); } catch { /* quota / private */ }
    dirty = true;
    schedule();
  } catch { /* diagnostics must never break the app */ }
}

export function diagEvents(): DiagEvent[] {
  return [...buf];
}

function schedule(): void {
  if (timer || !tokenFn) return;
  const wait = Math.max(1500, 8000 - (Date.now() - lastPushAt));
  timer = setTimeout(() => { timer = null; void push(); }, wait);
}

async function push(): Promise<void> {
  if (!dirty || !tokenFn) return;
  const token = tokenFn();
  if (!token || typeof navigator !== "undefined" && navigator.onLine === false) { schedule(); return; }
  dirty = false;
  lastPushAt = Date.now();
  try {
    await fetch(API_BASE + "/api/diag/push", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ build, ua: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 160) : "", events: buf.slice(-MAX) }),
      keepalive: true,
    });
  } catch {
    dirty = true; // try again on the next event
  }
}

/** Flush on the way out (tab close / hide) so the last events aren't lost. */
export function diagFlush(): void {
  if (!dirty || !tokenFn) return;
  const token = tokenFn();
  if (!token) return;
  try {
    const body = JSON.stringify({ build, ua: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 160) : "", events: buf.slice(-MAX) });
    // sendBeacon can't carry an Authorization header — use keepalive fetch
    void fetch(API_BASE + "/api/diag/push", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body, keepalive: true });
    dirty = false;
  } catch { /* best effort */ }
}
